import { useState, useEffect, useContext } from 'react'
import { useT } from '../hooks'
import AuthContext from 'contexte/AuthContext'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import TickerImage from 'components/DataComponents/TickerImage/TickerImage'
import SearchTicker from 'components/SearchTicker/SearchTicker'
import { launchError, launchSuccess } from 'components/notifications'
import { fetchTickers } from 'services/tickersServices'
import { fetchBBookTickers, addBBookTicker, removeBBookTicker, updateBBookTickerExtraLeverages } from 'services/bBookServices'
import { leverageLabel } from '../leverage'
import { recall, remember } from '../cache'
import ConfirmModal from 'components/ConfirmModal/ConfirmModal'
import BBookEmpty from './BBookEmpty'
import BBookIcon from './BBookIcon'
import BBookListToolbar, { BBookPager } from './BBookListToolbar'

const EXTRA_ERRORS = { margin_rate_not_below_lmax: 'extra_error_lmax', margin_rate_below_fee_floor: 'extra_error_fee_floor', margin_rate_max_two_decimals: 'extra_error_decimals', leverage_in_use: 'extra_error_in_use' }

// Instruments the house may take the other side of (is_b_book), each with the leverages the app offers on it: locked up to LMAX's
// margin, the house's own beyond it. Removing an instrument stops new b-book flow on it at once.
const BBookTickers = ({ onChange }) => {
  const { t } = useT()
  const { user } = useContext(AuthContext)

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState(10)
  const [tickers, setTickers] = useState(() => recall('tickers') ?? [])
  const [searchResults, setSearchResults] = useState([])
  const [selected, setSelected] = useState({})
  const [adding, setAdding] = useState(false)
  const [toRemove, setToRemove] = useState(null)
  const [removing, setRemoving] = useState(false)
  const [extraDrafts, setExtraDrafts] = useState({}) // ticker -> leverage typed for the b-book-only list, not saved yet
  const [savingExtra, setSavingExtra] = useState(null)

  // the whole list comes in one call: filtered and paged here, so typing costs no request
  useEffect(() => {
    const load = async () => {
      try {
        setTickers(remember('tickers', await fetchBBookTickers(user, '')))
      } catch (e) {
        launchError(t('notifications.b_book.tickers.fetch_error'))
      }
    }
    load()
  }, [])
  useEffect(() => setPage(0), [search, limit])

  const handleSearch = async (value) => {
    if (!value.length) return setSearchResults([])
    try {
      setSearchResults(await fetchTickers({ user, page: 0, limit: 10, search: value, leverage: true, type: 'lmax' }))
    } catch (e) {
      console.error(e)
    }
  }

  const add = async () => {
    if (!selected.id) return
    try {
      setAdding(true)
      const created = await addBBookTicker(user, selected.ticker)
      setTickers(prev => [created, ...prev])
      setSelected({})
      setSearchResults([])
      onChange()
      launchSuccess(t('notifications.b_book.tickers.add_success'))
    } catch (e) {
      launchError(t('notifications.b_book.tickers.add_error'))
    } finally {
      setAdding(false)
    }
  }

  // Leverages above LMAX's, b-book only: typed as a leverage (x500), stored as a margin rate below LMAX's (0.2 %); the whole list is saved each time.
  const saveExtra = async (item, margin_rates) => {
    try {
      setSavingExtra(item.ticker)
      const data = await updateBBookTickerExtraLeverages(user, item.ticker, margin_rates)
      setTickers(prev => prev.map((row) => (row.ticker === item.ticker ? { ...row, ...data } : row)))
      setExtraDrafts(prev => { const { [item.ticker]: _saved, ...rest } = prev; return rest })
      launchSuccess(t('notifications.b_book.tickers.extra_success', { ticker: item.ticker, labels: data.extra_rates.map(leverageLabel).join(', ') || '—' }))
    } catch (e) {
      launchError(t(`notifications.b_book.tickers.${EXTRA_ERRORS[e?.response?.data?.error] || 'extra_error'}`, { ticker: item.ticker }))
    } finally {
      setSavingExtra(null)
    }
  }

  const addExtra = (item) => {
    const leverage = Number(extraDrafts[item.ticker])
    if (!(leverage > 0)) return
    const rate = Math.round(10000 / leverage) / 100 // margin rate in %, 2 decimals like the margin_rate columns: x300 is not representable, x400 is
    if (!item.extra_rates.includes(rate)) saveExtra(item, [...item.extra_rates, rate])
  }

  // the highest b-book-only leverage the spread leaves room for, on a 2-decimal rate; null when nothing fits under LMAX's margin
  const extraCeiling = (item) => {
    if (!(item.min_margin_rate >= 0) || item.lmax_margin_rate === null) return null
    const rate = Math.max(0.01, Math.ceil(item.min_margin_rate * 100 - 1e-9) / 100)
    return rate < item.lmax_margin_rate ? rate : null
  }

  // one chip per leverage, lowest leverage first like the app: locked up to LMAX's margin, removable beyond it unless a position still uses it
  const leverageChip = (item, rate, busy) => {
    const label = leverageLabel(rate)
    const locked = item.lmax_margin_rate === null || rate >= item.lmax_margin_rate
    if (locked) {
      return (
        <span key={rate} className='b_book_chip b_book_chip_locked' title={t('pages.b_book.tickers.locked_label', { label })}>
          <BBookIcon name='lock' size={12} />{label}
        </span>
      )
    }
    const inUse = item.rates_in_use?.includes(rate)
    return (
      <span key={rate} className='b_book_chip' title={inUse ? t('pages.b_book.tickers.in_use', { label }) : undefined}>
        {label}
        <button type='button' className='b_book_chip_remove' disabled={busy || inUse}
          aria-label={inUse ? t('pages.b_book.tickers.in_use', { label }) : t('pages.b_book.tickers.extra_remove_label', { label, ticker: item.ticker })}
          onClick={() => saveExtra(item, item.extra_rates.filter((other) => other !== rate))}>×</button>
      </span>
    )
  }

  const leveragesCell = (item) => {
    const busy = savingExtra === item.ticker
    const ceiling = extraCeiling(item)
    const known = item.lmax_margin_rate !== null
    return (
      <div className='b_book_extra_leverages'>
        <span className='b_book_hint'>{known ? t('pages.b_book.tickers.extra_lmax', { label: leverageLabel(item.lmax_margin_rate) }) : t('pages.b_book.tickers.extra_unknown')}</span>
        <div className='b_book_chips'>
          {[...item.leverage_rates].sort((a, b) => b - a).map((rate) => leverageChip(item, rate, busy))}
          {known && (
            <>
              <input type='number' inputMode='numeric' min='1' step='1' className='b_book_chip_input' placeholder='x500' value={extraDrafts[item.ticker] ?? ''} disabled={busy}
                aria-label={t('pages.b_book.tickers.extra_add_label', { ticker: item.ticker })}
                onChange={(e) => setExtraDrafts(prev => ({ ...prev, [item.ticker]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') addExtra(item) }} />
              <button type='button' className='b_book_row_button' disabled={busy || !(Number(extraDrafts[item.ticker]) > 0)} aria-busy={busy || undefined} onClick={() => addExtra(item)}>
                {busy ? t('pages.b_book.saving') : t('pages.b_book.tickers.extra_add')}
              </button>
            </>
          )}
        </div>
        {known && <span className='b_book_hint'>{ceiling === null ? t('pages.b_book.tickers.extra_no_room') : t('pages.b_book.tickers.extra_max', { label: leverageLabel(ceiling) })}</span>}
      </div>
    )
  }

  const remove = async () => {
    try {
      setRemoving(true)
      await removeBBookTicker(user, toRemove)
      setTickers(prev => prev.filter((item) => item.ticker !== toRemove))
      onChange()
      launchSuccess(t('notifications.b_book.tickers.remove_success'))
    } catch (e) {
      launchError(t('notifications.b_book.tickers.remove_error'))
    } finally {
      setRemoving(false)
      setToRemove(null)
    }
  }

  const query = search.trim().toLowerCase()
  const filtered = (tickers || []).filter(({ ticker, name }) => !query || `${ticker} ${name || ''}`.toLowerCase().includes(query))
  const pageCount = Math.max(1, Math.ceil(filtered.length / limit))
  const tbody = filtered.slice(page * limit, (page + 1) * limit).map((item) => ({
    data: [
      <TickerImage ticker={item.ticker} />,
      leveragesCell(item),
      <div className='b_book_row_actions'>
        <button className='b_book_row_button b_book_row_button_danger' aria-label={t('pages.b_book.tickers.remove_label', { ticker: item.ticker })}
          onClick={(e) => { e.stopPropagation(); setToRemove(item.ticker) }}>
          {t('pages.b_book.tickers.remove')}
        </button>
      </div>
    ],
    utils: { id: item.ticker }
  }))

  return (
    <section aria-labelledby='b-book-tickers-title'>
      <div className='b_book_title_component'>
        <h2 id='b-book-tickers-title'>{t('pages.b_book.tickers.title')} <span className='b_book_count'>{tickers.length}</span></h2>
      </div>
      <p className='b_book_hint'>{t('pages.b_book.tickers.hint')}</p>

      <div className='b_book_card'>
        <div className='b_book_add_row'>
          <SearchTicker setSelectedTicker={setSelected} selectedTicker={selected} tickers={searchResults} handleSearch={handleSearch} label={t('pages.b_book.tickers.search_placeholder')} />
          <button className='b_book_action_button' disabled={!selected.id || adding} aria-busy={adding || undefined} onClick={add}>
            {adding ? t('pages.b_book.saving') : t('pages.b_book.tickers.add')}
          </button>
        </div>
        {tickers.length > 0 && (
          <BBookListToolbar searchLabel={t('pages.b_book.tickers.filter_label')} searchPlaceholder={t('pages.b_book.tickers.filter_placeholder')}
            search={search} setSearch={setSearch} limit={limit} setLimit={setLimit} />
        )}
        {tbody.length ? (
          <>
            <div className="table-container"><div className="table-column">
              <Tableau thead={[t('pages.b_book.tickers.table.ticker'), t('pages.b_book.tickers.table.leverage'), t('pages.b_book.tickers.table.action')]} tbody={tbody} />
            </div></div>
            <BBookPager page={page} pageCount={pageCount} onChange={setPage} />
          </>
        ) : (
          <BBookEmpty title={t(search ? 'pages.b_book.tickers.empty_search' : 'pages.b_book.tickers.empty')} hint={search ? null : t('pages.b_book.tickers.empty_hint')} />
        )}
      </div>

      <ConfirmModal
        isOpen={!!toRemove}
        loading={removing}
        title={t('pages.b_book.tickers.confirm_title', { ticker: toRemove })}
        message={t('pages.b_book.tickers.confirm_message', { ticker: toRemove })}
        confirmLabel={t('pages.b_book.tickers.remove')}
        cancelLabel={t('pages.b_book.positions.cancel')}
        onConfirm={remove}
        onClose={() => setToRemove(null)}
      />
    </section>
  )
}

export default BBookTickers
